import { useSelector, useDispatch } from 'react-redux';
import { useState, useMemo, useEffect, useRef } from 'react';
import { fetchCoachAthletesWithAssessments } from '../../redux/slices/coachSlice';
import { downloadElementAsPDF } from '../../utils/exportPDF';
import Dropdown from '../Dropdown/Dropdown';
import DownloadPDFButton from '../DownloadPDFButton/DownloadPDFButton';
import { AnimatePresence, motion } from 'framer-motion';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';

import styles from './AthleteLeaderboard.module.css';
import globalButtonStyles from '../../styles/ButtonGradient.module.css';

const AthleteLeaderboard = () => {
  const dispatch = useDispatch();
  const athletesWithAssessments = useSelector(state => state.coach.athletesWithAssessments);
  const selectedSport = useSelector(state => state.quiz.sport);
  const loadingLeaderboard = useSelector(state => state.coach.loadingLeaderboard);

  const [sortBy, setSortBy] = useState('level-1');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expanded, setExpanded] = useState(false);

  const leaderboardRef = useRef(null);

  useEffect(() => {
    if (selectedSport) {
      dispatch(fetchCoachAthletesWithAssessments(selectedSport));
    }
  }, [dispatch, selectedSport]);

  const sortedAthletes = useMemo(() => {
    if (!athletesWithAssessments || !selectedSport) return [];

    return [...athletesWithAssessments]
      .filter(a => a.athlete?.sports?.includes(selectedSport))
      .sort((a, b) => {
        const level = parseInt(sortBy.split('level-')[1], 10);
        const aValue = a.highestScoreByLevel?.find(lvl => lvl.level === level)?.scorePercent || 0;
        const bValue = b.highestScoreByLevel?.find(lvl => lvl.level === level)?.scorePercent || 0;
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });
  }, [athletesWithAssessments, sortBy, sortDirection, selectedSport]);

  const toggleSortDirection = () => {
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleDownloadLeaderboard = () => {
    downloadElementAsPDF(leaderboardRef.current, {
      filename: 'athlete-leaderboard.pdf',
      pdfOrientation: 'landscape',
    });
  };

  const previewLimit = 3;

  if (!selectedSport) {
    return (
      <div className={styles.previewCard}>
        <h2 className={styles.title}>Athlete Leaderboard</h2>
        <p className={styles.message}>Please select a sport to view the leaderboard.</p>
      </div>
    );
  }

  if (loadingLeaderboard) {
    return (
      <div className={styles.previewCard}>
        <h2 className={styles.title}>Athlete Leaderboard</h2>
        <p className={styles.message}>Loading leaderboard...</p>
      </div>
    );
  }

  const renderSortButton = () => (
    <button className={globalButtonStyles.button} onClick={toggleSortDirection}>
      {sortDirection === 'asc' ? <><FaArrowUp /> Ascending</> : <><FaArrowDown /> Descending</>}
    </button>
  );

  return (
    <>
      <div className={styles.previewCard}>
        <h2 className={styles.title}>Athlete Leaderboard</h2>

        <div className={styles.controls}>
          <Dropdown
            options={[
              { label: 'Level 1 Score', value: 'level-1' },
              { label: 'Level 2 Score', value: 'level-2' },
              { label: 'Level 3 Score', value: 'level-3' },
              { label: 'Level 4 Score', value: 'level-4' },
              { label: 'Level 5 Score', value: 'level-5' },
            ]}
            value={sortBy}
            onChange={(val) => {
              setSortBy(val);
              setSortDirection('desc');
            }}
            placeholder="Sort By"
          />
          {renderSortButton()}
        </div>

        <div className={styles.tablePreview}>
        {sortedAthletes.slice(0, previewLimit).map((item) => {
          const level = parseInt(sortBy.split('level-')[1], 10);
          const value = item.highestScoreByLevel?.find(lvl => lvl.level === level)?.scorePercent?.toFixed(1) || '-';
          return (
            <div key={item.athlete._id} className={styles.tablePreviewRow}>
              <span>{item.athlete.firstName} {item.athlete.lastName}</span>
              <span>{value}%</span>
            </div>
          );
        })}
        </div>

        <button className={styles.expandButton} onClick={() => setExpanded(true)}>
          Expand Leaderboard ▼
        </button>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            ref={leaderboardRef}
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className={styles.overlayHeader}>
              <h2>Full Leaderboard</h2>
              <DownloadPDFButton onClick={handleDownloadLeaderboard} />
              <button className={styles.closeButton} onClick={() => setExpanded(false)}>
                ✕
              </button>
            </div>

            <div className={styles.controls}>
              <Dropdown
                options={[
                  { label: 'GScore', value: 'gScore' },
                  { label: 'Focus', value: 'trait-focus' },
                  { label: 'Resilience', value: 'trait-resilience' },
                  { label: 'Discipline', value: 'trait-discipline' },
                  { label: 'Coachability', value: 'trait-coachability' },
                  { label: 'Competitiveness', value: 'trait-competitiveness' },
                  { label: 'Level 1 Score', value: 'level-1' },
                  { label: 'Level 2 Score', value: 'level-2' },
                  { label: 'Level 3 Score', value: 'level-3' },
                  { label: 'Level 4 Score', value: 'level-4' },
                  { label: 'Level 5 Score', value: 'level-5' },
                ]}
                value={sortBy}
                onChange={(val) => {
                  setSortBy(val);
                  setSortDirection('desc');
                }}
                placeholder="Sort By"
              />
              {renderSortButton()}
            </div>

            <table className={styles.fullTable}>
              <thead>
                <tr>
                  <th>Athlete</th>
                  <th>L1</th>
                  <th>L2</th>
                  <th>L3</th>
                  <th>L4</th>
                  <th>L5</th>
                </tr>
              </thead>
              <tbody>
                {sortedAthletes.map((item) => (
                  <tr key={item.athlete._id}>
                    <td>{item.athlete.firstName} {item.athlete.lastName}</td>
                    {[1, 2, 3, 4, 5].map(level => (
                      <td key={level}>
                        {item.highestScoreByLevel?.find(lvl => lvl.level === level)?.scorePercent?.toFixed(1) ?? '-'}%
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AthleteLeaderboard;